import { useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { format, isBefore, startOfDay } from 'date-fns';
import { sv } from 'date-fns/locale';
import { BookOpen, CalendarDays, Bed, User, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { getBookingListStatus, type BookingListStatus } from '@/lib/booking/listStatus';

interface UnifiedBooking {
  id: string;
  type: 'activity' | 'accommodation';
  title: string;
  guestName: string | null;
  guestEmail: string;
  date: string;
  endDate?: string;
  dateLabel: string;
  status: string;
  spots?: number;
  accommodationId?: string;
  /** Derived host-facing status */
  listStatus: BookingListStatus;
}

function isUpcoming(b: UnifiedBooking): boolean {
  const today = startOfDay(new Date());
  const compareDate = b.endDate ? new Date(b.endDate) : new Date(b.date);
  return !isBefore(compareDate, today) && b.status !== 'cancelled';
}

// --- Badge tone mapping ---
const toneStyles: Record<BookingListStatus['tone'], string> = {
  attention: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  progress: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
  neutral: 'bg-muted text-muted-foreground border-border',
  destructive: '',
};

export default function ProducerBookingsHubPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPast, setShowPast] = useState(false);

  const initialTab = searchParams.get('type') === 'accommodation'
    ? 'accommodation'
    : searchParams.get('type') === 'activity'
    ? 'activity'
    : 'all';
  const filterAccommodationId = searchParams.get('accommodationId') || null;

  const [tab, setTab] = useState(initialTab);

  const handleTabChange = (newTab: string) => {
    setTab(newTab);
    const next = new URLSearchParams();
    if (newTab !== 'all') next.set('type', newTab);
    setSearchParams(next, { replace: true });
  };

  // Fetch event bookings
  const { data: eventBookings = [], isLoading: evLoading } = useQuery({
    queryKey: ['producer-event-bookings-hub', farmId],
    queryFn: async () => {
      if (!farmId) return [];
      const { data, error } = await supabase
        .from('event_bookings')
        .select(`
          id, guest_name, guest_email, status, spots, created_at,
          producer_events!event_bookings_event_id_fkey ( title )
        `)
        .eq('producer_id', farmId)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []).map((b: any): UnifiedBooking => ({
        id: b.id,
        type: 'activity',
        title: b.producer_events?.title ?? 'Aktivitet',
        guestName: b.guest_name,
        guestEmail: b.guest_email,
        date: b.created_at,
        dateLabel: format(new Date(b.created_at), 'd MMM yyyy', { locale: sv }),
        status: b.status,
        spots: b.spots,
        listStatus: getBookingListStatus({ type: 'activity', status: b.status }),
      }));
    },
    enabled: !!farmId,
  });

  // Fetch accommodation bookings (with payment fields)
  const { data: accBookings = [], isLoading: accLoading } = useQuery({
    queryKey: ['producer-accommodation-bookings-hub', farmId],
    queryFn: async () => {
      if (!farmId) return [];
      const { data, error } = await supabase
        .from('accommodation_bookings')
        .select(`
          id, accommodation_id, guest_name, guest_email, status, guests,
          check_in, check_out, created_at,
          payment_status, amount_paid_ore, requires_prepayment, prepayment_amount_ore,
          producer_accommodations!accommodation_bookings_accommodation_id_fkey ( title )
        `)
        .eq('producer_id', farmId)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      const rows = data ?? [];
      if (rows.length === 0) return [];

      // Aggregate payments from canonical source
      const bookingIds = rows.map((b: any) => b.id);
      const { data: paymentRows } = await supabase
        .from('accommodation_booking_payments')
        .select('booking_id, amount_ore')
        .in('booking_id', bookingIds);

      const paidByBooking = new Map<string, number>();
      for (const p of paymentRows ?? []) {
        paidByBooking.set(p.booking_id, (paidByBooking.get(p.booking_id) ?? 0) + p.amount_ore);
      }

      return rows.map((b: any): UnifiedBooking => {
        const checkIn = new Date(b.check_in);
        const checkOut = new Date(b.check_out);
        const totalStayOre: number | null = null;
        const aggregatedPaidOre = paidByBooking.get(b.id) ?? 0;

        return {
          id: b.id,
          type: 'accommodation',
          title: b.producer_accommodations?.title ?? 'Boende',
          guestName: b.guest_name,
          guestEmail: b.guest_email,
          date: b.check_in,
          endDate: b.check_out,
          dateLabel: `${format(checkIn, 'd MMM', { locale: sv })} – ${format(checkOut, 'd MMM yyyy', { locale: sv })}`,
          status: b.status,
          spots: b.guests,
          accommodationId: b.accommodation_id,
          listStatus: getBookingListStatus({
            type: 'accommodation',
            status: b.status,
            paymentStatus: b.payment_status,
            amountPaidOre: aggregatedPaidOre,
            totalStayOre,
            requiresPrepayment: b.requires_prepayment,
            prepaymentAmountOre: b.prepayment_amount_ore,
          }),
        };
      });
    },
    enabled: !!farmId,
  });

  const isLoading = evLoading || accLoading;

  const allBookings = useMemo(() => {
    const merged = [...eventBookings, ...accBookings];
    merged.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return merged;
  }, [eventBookings, accBookings]);

  const filtered = useMemo(() => {
    let result = allBookings;
    if (tab === 'activity') result = result.filter(b => b.type === 'activity');
    if (tab === 'accommodation') result = result.filter(b => b.type === 'accommodation');
    if (filterAccommodationId && tab === 'accommodation') {
      result = result.filter(b => b.accommodationId === filterAccommodationId);
    }
    return result;
  }, [allBookings, tab, filterAccommodationId]);

  const upcoming = useMemo(() => filtered.filter(isUpcoming), [filtered]);
  const past = useMemo(() =>
    filtered.filter(b => !isUpcoming(b)).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [filtered],
  );

  const nextBooking = upcoming[0] ?? null;

  const filterTitle = useMemo(() => {
    if (!filterAccommodationId) return null;
    const match = accBookings.find(b => b.accommodationId === filterAccommodationId);
    return match?.title || null;
  }, [filterAccommodationId, accBookings]);

  return (
    <ProducerLayout farmId={farmId} title="Bokningar">
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Bokningar</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Alla inkommande bokningar — aktiviteter och boende
        </p>
      </div>

      {/* Overview cards */}
      {!isLoading && allBookings.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card>
            <CardContent className="py-4 px-4">
              <p className="text-2xl font-bold text-foreground">{upcoming.length}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Kommande bokningar</p>
            </CardContent>
          </Card>
          {nextBooking && (
            <Card className="col-span-2 sm:col-span-2 border-primary/20 bg-primary/[0.03]">
              <CardContent className="py-4 px-4">
                <p className="text-xs text-muted-foreground mb-1">Nästa bokning</p>
                <p className="text-sm font-medium text-foreground truncate">{nextBooking.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-sm text-foreground/80">{nextBooking.dateLabel}</p>
                  {nextBooking.listStatus.tone === 'attention' && (
                    <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                      {nextBooking.listStatus.label}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {nextBooking.guestName || nextBooking.guestEmail}
                  {nextBooking.spots && nextBooking.spots > 1 && ` · ${nextBooking.spots} ${nextBooking.type === 'accommodation' ? 'gäster' : 'platser'}`}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="all">
            Alla
            {allBookings.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-xs">{allBookings.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="activity">
            <CalendarDays className="h-3.5 w-3.5 mr-1" />
            Aktiviteter
          </TabsTrigger>
          <TabsTrigger value="accommodation">
            <Bed className="h-3.5 w-3.5 mr-1" />
            Boende
          </TabsTrigger>
        </TabsList>

        {filterAccommodationId && tab === 'accommodation' && (
          <div className="flex items-center gap-2 mt-3">
            <Badge variant="outline" className="text-xs">
              {filterTitle || 'Filtrerat boende'}
            </Badge>
            <button
              className="text-xs text-muted-foreground underline hover:text-foreground"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('accommodationId');
                setSearchParams(next, { replace: true });
              }}
            >
              Visa alla boendebokningar
            </button>
          </div>
        )}

        <TabsContent value={tab} className="mt-4 space-y-6">
          {isLoading ? (
            <p className="text-muted-foreground text-sm py-8 text-center">Laddar bokningar…</p>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-muted-foreground text-sm">
                  {tab === 'all'
                    ? 'Inga bokningar ännu'
                    : tab === 'activity'
                    ? 'Inga aktivitetsbokningar ännu'
                    : 'Inga boendebokningar ännu'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Upcoming */}
              {upcoming.length > 0 && (
                <section>
                  <h2 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wide">
                    Kommande ({upcoming.length})
                  </h2>
                  <div className="space-y-2">
                    {upcoming.map((b) => (
                      <BookingCard key={b.id} booking={b} highlight={b.id === nextBooking?.id} farmId={farmId} />
                    ))}
                  </div>
                </section>
              )}

              {/* Past */}
              {past.length > 0 && (
                <section>
                  <button
                    onClick={() => setShowPast(!showPast)}
                    className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wide hover:text-foreground transition-colors"
                  >
                    Tidigare ({past.length})
                    {showPast ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                  {showPast && (
                    <div className="space-y-2">
                      {past.map((b) => (
                        <BookingCard key={b.id} booking={b} muted farmId={farmId} />
                      ))}
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
    </ProducerLayout>
  );
}

// --- Booking card ---
function BookingCard({ booking: b, highlight, muted, farmId }: { booking: UnifiedBooking; highlight?: boolean; muted?: boolean; farmId?: string }) {
  const navigate = useNavigate();
  const ls = b.listStatus;

  const isClickable = b.type === 'accommodation' && !!farmId;

  return (
    <Card
      className={cn(
        'transition-shadow',
        highlight && 'border-primary/30 bg-primary/[0.02] shadow-sm',
        muted && 'opacity-70',
        isClickable && 'cursor-pointer hover:shadow-md',
      )}
      onClick={isClickable ? () => navigate(`/app/producer/farm/${farmId}/bookings/accommodation/${b.id}`) : undefined}
    >
      <CardContent className="py-3.5 px-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          {/* Left: type icon + info */}
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className={cn(
              'shrink-0 mt-0.5 p-1.5 rounded-lg',
              b.type === 'accommodation' ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-violet-50 dark:bg-violet-950/30',
            )}>
              {b.type === 'accommodation' ? (
                <Bed className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              ) : (
                <CalendarDays className="h-4 w-4 text-violet-600 dark:text-violet-400" />
              )}
            </div>
            <div className="min-w-0 space-y-0.5">
              <p className="text-sm font-medium text-foreground truncate">{b.title}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {b.guestName || b.guestEmail}
                </span>
                {b.spots && b.spots > 1 && (
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {b.type === 'accommodation' ? `${b.spots} gäster` : `${b.spots} platser`}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right: date + status */}
          <div className="flex flex-col items-end gap-1 pl-10 sm:pl-0 shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground tabular-nums">
                {b.dateLabel}
              </span>
              <Badge
                variant={ls.tone === 'destructive' ? 'destructive' : 'outline'}
                className={cn(
                  'text-xs whitespace-nowrap',
                  ls.tone !== 'destructive' && toneStyles[ls.tone],
                )}
              >
                {ls.label}
              </Badge>
            </div>
            {ls.paymentSummary && (
              <span className="text-xs text-muted-foreground tabular-nums">
                {ls.paymentSummary}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
